/**
 * Badge indicador de status de sincronização com barra de progresso
 */
import { RefreshCw, Check, AlertCircle, Loader2, Wifi, WifiOff, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEffect, useState, useRef } from 'react';
import type { SyncStatus } from '../hooks/useSyncEvents';

// Tempo estimado de sync em segundos (baseado em observações)
const ESTIMATED_SYNC_TIME_SECONDS = 70;

interface SyncStatusBadgeProps {
  status: SyncStatus;
  onManualSync?: () => void;
  onQuickRefresh?: () => void;
  isTriggering?: boolean;
  isRefreshing?: boolean;
  className?: string;
}

export function SyncStatusBadge({
  status,
  onManualSync,
  onQuickRefresh,
  isTriggering = false,
  isRefreshing = false,
  className,
}: SyncStatusBadgeProps) {
  const { isConnected, isSyncing, lastSync, currentProgress, lastError } = status;
  
  // Estado para barra de progresso baseada em tempo
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const syncStartTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Qualquer operação em andamento
  const isBusy = isTriggering || isRefreshing || isSyncing;

  // Gerencia o timer de progresso
  useEffect(() => {
    if (isSyncing) {
      // Inicia o timer quando começa a sincronizar
      if (!syncStartTimeRef.current) {
        syncStartTimeRef.current = Date.now();
        setElapsedSeconds(0);
      }
      
      intervalRef.current = setInterval(() => {
        if (syncStartTimeRef.current) {
          const elapsed = Math.floor((Date.now() - syncStartTimeRef.current) / 1000);
          setElapsedSeconds(elapsed);
        }
      }, 100); // Atualiza a cada 100ms para animação suave
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      // Reset quando termina
      syncStartTimeRef.current = null;
      setElapsedSeconds(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [isSyncing]);

  // Calcula progresso baseado em tempo (0-100)
  const timeBasedProgress = Math.min(
    (elapsedSeconds / ESTIMATED_SYNC_TIME_SECONDS) * 100,
    95 // Nunca chega a 100% até completar
  );

  // Formata timestamp para exibição
  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return 'Nunca';
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Formata segundos para MM:SS
  const formatElapsed = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Estado atual
  const getStatusInfo = () => {
    if (lastError) {
      return {
        icon: AlertCircle,
        text: 'Erro na sync',
        color: 'text-red-500',
        bgColor: 'bg-red-500/10',
        progressColor: 'bg-red-500',
      };
    }
    if (isRefreshing) {
      return {
        icon: Loader2,
        text: 'Atualizando...',
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-500/10',
        progressColor: 'bg-emerald-500',
        spin: true,
      };
    }
    if (isSyncing) {
      return {
        icon: Loader2,
        text: `Sincronizando...`,
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10',
        progressColor: 'bg-blue-500',
        spin: true,
      };
    }
    if (lastSync) {
      return {
        icon: Check,
        text: `Sync: ${formatTime(lastSync)}`,
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-500/10',
        progressColor: 'bg-emerald-500',
      };
    }
    return {
      icon: RefreshCw,
      text: 'Aguardando sync',
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
      progressColor: 'bg-muted-foreground',
    };
  };

  const statusInfo = getStatusInfo();
  const Icon = statusInfo.icon;

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center gap-2">
        {/* Indicador de conexão SSE */}
        <div
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded-full text-xs',
            isConnected ? 'text-emerald-500' : 'text-muted-foreground'
          )}
          title={isConnected ? 'Conectado ao servidor' : 'Desconectado'}
        >
          {isConnected ? (
            <Wifi className="w-3 h-3" />
          ) : (
            <WifiOff className="w-3 h-3" />
          )}
        </div>

        {/* Status da sync */}
        <div
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
            statusInfo.bgColor,
            statusInfo.color
          )}
          title={lastError || undefined}
        >
          <Icon className={cn('w-3.5 h-3.5', statusInfo.spin && 'animate-spin')} />
          <span>{statusInfo.text}</span>
          {isSyncing && (
            <span className="flex items-center gap-1 ml-1 text-[10px] opacity-80">
              <Clock className="w-3 h-3" />
              {formatElapsed(elapsedSeconds)}
            </span>
          )}
        </div>

        {/* Botão de sync manual */}
        {onManualSync && !isBusy && (
          <button
            onClick={onManualSync}
            disabled={isBusy}
            className={cn(
              'flex items-center gap-1 px-2 py-1 rounded text-xs',
              'bg-primary/10 text-primary hover:bg-primary/20',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors'
            )}
            title="Sincronizar dados externos e atualizar"
          >
            <RefreshCw className={cn('w-3 h-3', isBusy && 'animate-spin')} />
            <span>Sync</span>
          </button>
        )}
      </div>

      {/* Barra de progresso - aparece durante sync */}
      {isSyncing && (
        <div className="w-full max-w-xs">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-0.5">
            <span>Buscando dados externos...</span>
            <span>{Math.round(timeBasedProgress)}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-300 ease-out',
                statusInfo.progressColor,
                // Adiciona efeito de "pulse" enquanto sincroniza
                'animate-pulse'
              )}
              style={{ width: `${timeBasedProgress}%` }}
            />
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5 text-center">
            Tempo estimado: ~{Math.max(0, ESTIMATED_SYNC_TIME_SECONDS - elapsedSeconds)}s restantes
          </div>
        </div>
      )}
    </div>
  );
}
